/**
 * Jyotish Tarang — My Report Page (Phase 3, 10 Jul 2026)
 * Single card: trigger generation, poll status, download/regenerate.
 * Replaces the 3 old scoped "Export PDF" buttons (ChartView main tab,
 * Shadbala tab, Ashtakavarga tab) and ChartView's old "Reports" tab.
 * The primary card is the one tier-aware "comprehensive" report built by
 * report_generator.py / report_pdf.py (Phase 2, 09 Jul 2026) — sections are
 * gated server-side by tier_config, not by anything in this file. A smaller
 * "More one-time reports" section below preserves the pre-existing a-la-carte
 * career / dosha / varshphal purchases that used to live in ChartView's
 * Reports tab.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../App';

declare global { interface Window { Razorpay: any; } }

function getToken() {
  return sessionStorage.getItem('access_token') || localStorage.getItem('access_token') || '';
}

interface Chart { id: number; name: string; dob: string; }
interface CatalogItem {
  type: string; title: string; description: string;
  price_inr: number; delivery_mins?: number;
}
type RStatus = 'idle' | 'generating' | 'ready' | 'error';

export default function ReportPage() {
  const { user }  = useAuth();
  const navigate  = useNavigate();
  const { t } = useTranslation();
  const [charts,  setCharts]  = useState<Chart[]>([]);
  const [chartId, setChartId] = useState<number | null>(null);
  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [status, setStatus] = useState<Record<string, RStatus>>({});
  const [tokens, setTokens] = useState<Record<string, string>>({});
  const [orderIds, setOrderIds] = useState<Record<string, string>>({});

  const TIER_BLURB: Record<string, string> = {
    free:     t('reportPage.tierBlurb.free'),
    silver:   t('reportPage.tierBlurb.free'),
    pro:      t('reportPage.tierBlurb.gold'),
    gold:     t('reportPage.tierBlurb.gold'),
    expert:   t('reportPage.tierBlurb.platinum'),
    platinum: t('reportPage.tierBlurb.platinum'),
  };

  const tier = user?.tier || 'free';
  const tierLabel = ({ free:t('tier.free'), silver:t('tier.free'), pro:t('tier.gold'), gold:t('tier.gold'), expert:t('tier.platinum'), platinum:t('tier.platinum') } as Record<string,string>)[tier] || tier;

  useEffect(() => {
    Promise.all([
      axios.get('/api/charts/').catch(() => ({ data: [] })),
      axios.get('/api/reports/catalog').catch(() => ({ data: { reports: [] } })),
    ]).then(([cr, catr]) => {
      setCharts(cr.data || []);
      if (cr.data?.length) setChartId(cr.data[0].id);
      setCatalog(catr.data?.reports || []);
    }).finally(() => setLoading(false));
  }, []);

  const refreshMyReports = useCallback((cid: number) => {
    axios.get('/api/reports/my').then(r => {
      const rows: any[] = r.data?.reports || [];
      const latestByType: Record<string, any> = {};
      rows.filter(row => Number(row.chart_id) === Number(cid)).forEach(row => {
        // rows are DESC by created_at already — first hit per type is latest
        if (!latestByType[row.report_type]) latestByType[row.report_type] = row;
      });
      const st: Record<string, RStatus> = {};
      const tk: Record<string, string> = {};
      Object.entries(latestByType).forEach(([type, row]: [string, any]) => {
        if (row.status === 'generated') { st[type] = 'ready'; tk[type] = row.token; }
        else if (row.status === 'failed') { st[type] = 'error'; }
        else if (row.status === 'generating' || row.status === 'paid') { st[type] = 'generating'; }
      });
      setStatus(st);
      setTokens(tk);
    }).catch(() => {});
  }, []);

  useEffect(() => { if (chartId) refreshMyReports(chartId); }, [chartId, refreshMyReports]);

  function pollStatus(reportType: string, orderId: string) {
    let attempts = 0;
    const maxAttempts = reportType === 'comprehensive' ? 300 : 90; // comprehensive can take 15-20 min
    const poll = setInterval(async () => {
      attempts++;
      if (attempts > maxAttempts) { clearInterval(poll); return; }
      try {
        const token = getToken();
        const stRes = await fetch(`/api/reports/status/${orderId}`, { headers: { Authorization: `Bearer ${token}` } });
        const stData = await stRes.json();
        if (stData.status === 'generated') {
          clearInterval(poll);
          setTokens(s => ({ ...s, [reportType]: stData.token }));
          setStatus(s => ({ ...s, [reportType]: 'ready' }));
        } else if (stData.status === 'failed') {
          clearInterval(poll);
          setStatus(s => ({ ...s, [reportType]: 'error' }));
        }
      } catch { /* keep polling */ }
    }, 4000);
  }

  async function handleBuy(reportType: string) {
    if (!chartId) return;
    const token = getToken();
    setStatus(s => ({ ...s, [reportType]: 'generating' }));
    try {
      const orderRes = await fetch('/api/reports/order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ report_type: reportType, chart_id: chartId }),
      });
      const orderData = await orderRes.json();
      if (!orderData.order_id) throw new Error(orderData.error || t('reportPage.errors.orderFailed'));

      // Admin complimentary generation (11 Jul 2026) -- server already
      // verified role and queued the report; skip Razorpay entirely and go
      // straight to polling.
      if (orderData.admin_free) {
        setOrderIds(s => ({ ...s, [reportType]: orderData.order_id }));
        pollStatus(reportType, orderData.order_id);
        return;
      }

      const item = catalog.find(c => c.type === reportType);
      const options: any = {
        key: orderData.key_id, order_id: orderData.order_id, amount: orderData.amount,
        currency: orderData.currency || 'INR', name: 'Jyotish Tarang',
        description: item?.title || reportType, image: '/static/img/logo.png',
        prefill: { name: user?.name, email: user?.email },
        theme: { color: '#FF9933' },
        handler: async (response: any) => {
          try {
            const verRes = await fetch('/api/reports/verify', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                report_type: reportType, chart_id: chartId,
              }),
            });
            const verData = await verRes.json();
            if (!verData.order_id) throw new Error(verData.error || t('reportPage.errors.verificationFailed'));
            setOrderIds(s => ({ ...s, [reportType]: verData.order_id }));
            pollStatus(reportType, verData.order_id);
          } catch (err) {
            console.error('Report verification error', err);
            setStatus(s => ({ ...s, [reportType]: 'error' }));
          }
        },
        modal: { ondismiss: () => setStatus(s => ({ ...s, [reportType]: 'idle' })) },
      };

      if (!window.Razorpay) {
        await new Promise<void>((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://checkout.razorpay.com/v1/checkout.js';
          s.onload = () => resolve();
          s.onerror = () => reject(new Error(t('reportPage.errors.razorpayLoadFailed')));
          document.head.appendChild(s);
        });
      }
      const rp = new window.Razorpay(options);
      rp.open();
    } catch (err: any) {
      console.error('Report order error', err);
      alert(err.message || t('reportPage.errors.couldNotStart'));
      setStatus(s => ({ ...s, [reportType]: 'idle' }));
    }
  }

  async function handleDownload(reportType: string) {
    const token = tokens[reportType];
    const accessToken = getToken();
    if (!token) return;
    try {
      const r = await fetch(`/api/reports/download/${token}`, { headers: { Authorization: `Bearer ${accessToken}` } });
      if (!r.ok) {
        // Bug fix (11 Jul 2026): previously this fell through to r.blob() even on a
        // failed response, silently "downloading" the raw JSON error body renamed
        // to .pdf. Most common cause: the download token is a 48h-expiry link and
        // the report was generated more than 48h ago — the PDF itself may still
        // exist, the link just needs refreshing via Regenerate.
        let msg = t('reportPage.errors.linkExpired');
        try { const d = await r.json(); if (d?.error) msg = d.error; } catch { /* ignore */ }
        setStatus(s => ({ ...s, [reportType]: 'error' }));
        alert(`${msg}\n\n${t('reportPage.errors.clickRegenerate')}`);
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `JT_${reportType}_Report.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert(t('reportPage.errors.downloadFailed'));
    }
  }

  if (loading) return <div className="loading-page"><div className="spinner"/>{t('reportPage.loading')}</div>;

  if (charts.length === 0) {
    return (
      <div className="page-wrap container-sm" style={{ textAlign: 'center', paddingTop: 40 }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
        <h3 style={{ color: 'var(--indigo)', marginBottom: 8 }}>{t('reportPage.noCharts')}</h3>
        <p style={{ color: 'var(--ink3)', marginBottom: 16, fontSize: 13 }}>
          {t('reportPage.noChartsBody')}
        </p>
        <button className="btn btn-primary" onClick={() => navigate('/chart/new')}>+ {t('nav.newChart')}</button>
      </div>
    );
  }

  const comp = catalog.find(c => c.type === 'comprehensive');
  const compStatus: RStatus = status['comprehensive'] || 'idle';
  // Phase 5 (14 Jul 2026, one-time-payments): 'basic' added -- it has a
  // real report_catalog row (Phase 4, v4.3) but wasn't in this hardcoded
  // list, so it would never have rendered here despite being purchasable.
  // Listed first since it's the cheapest/lightest option.
  const otherTypes = ['basic', 'career', 'dosha', 'varshphal'];
  const chartName = charts.find(c => c.id === chartId)?.name;

  return (
    <div className="page-wrap">
      <div className="container">

        {charts.length > 1 && (
          <div className="jt-vault-row" style={{ marginBottom: 16 }}>
            <span className="jt-vault-label">{t('reportPage.chart')}:</span>
            {charts.map((c, i) => (
              <div key={c.id} className={`jt-vc${c.id === chartId ? ' active' : ''}`}
                   onClick={() => setChartId(c.id)} style={{ cursor: 'pointer' }}>
                <div className="jt-vc-av" style={{ background: i === 0 ? 'var(--sfb)' : 'var(--indigo)' }}>
                  {c.name.charAt(0).toUpperCase()}
                </div>
                {c.name.split(' ')[0]}
              </div>
            ))}
          </div>
        )}

        {/* ── Primary card: the one tier-aware report ── */}
        <div className="card" style={{ border: '1.5px solid var(--goldb)', marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h2 style={{ fontFamily: 'var(--font-display)', color: 'var(--indigo)', marginBottom: 4 }}>
                📄 {t('reportPage.myReport')}{chartName ? ` — ${chartName}` : ''}
              </h2>
              <span className="jt-report-plat-badge">{tierLabel} {t('tier.planSuffix') === 'Plan' ? 'tier' : t('tier.planSuffix')}</span>
            </div>
            {comp && <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--sfb)', fontFamily: 'var(--font-mono)' }}>₹{comp.price_inr}</div>}
          </div>

          <p style={{ fontSize: 13, color: 'var(--ink2)', margin: '12px 0' }}>
            {TIER_BLURB[tier] || TIER_BLURB.free}
          </p>

          {compStatus === 'generating' && (
            <div className="info-box info-saffron" style={{ fontSize: 12 }}>
              ⏳ {t('reportPage.generatingNote')}
            </div>
          )}
          {compStatus === 'error' && (
            <div className="info-box info-red" style={{ fontSize: 12 }}>⚠ {t('reportPage.generationFailed')}</div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
            {compStatus === 'ready' ? (
              <>
                <button className="btn btn-primary" onClick={() => handleDownload('comprehensive')}>⬇ {t('reportPage.downloadPdf')}</button>
                <button className="btn btn-ghost" onClick={() => handleBuy('comprehensive')}>↻ {t('reportPage.regenerate')}</button>
              </>
            ) : (
              <button className="btn btn-primary" disabled={compStatus === 'generating'}
                      onClick={() => handleBuy('comprehensive')}>
                {compStatus === 'generating' ? t('reportPage.generatingShort') : compStatus === 'error' ? t('reportPage.tryAgain') : `📄 ${t('reportPage.generateMyReport')}`}
              </button>
            )}
          </div>
        </div>

        {/* ── Secondary: other one-time a-la-carte reports ── */}
        <h3 style={{ fontSize: 14, color: 'var(--indigo)', marginBottom: 10, fontFamily: 'var(--font-display)' }}>
          {t('reportPage.moreReports')}
        </h3>
        {otherTypes.map(type => {
          const item = catalog.find(c => c.type === type);
          if (!item) return null;
          const st = status[type] || 'idle';
          return (
            <div key={type} className="jt-rrow" style={{ cursor: 'default' }}>
              <span className="jt-rico">{type === 'basic' ? '📜' : type === 'career' ? '💼' : type === 'dosha' ? '🪬' : '📅'}</span>
              <div style={{ flex: 1 }}>
                <div className="jt-rname">{item.title}</div>
                <div style={{ fontSize: 11, color: 'var(--ink3)' }}>{item.description}</div>
                {st === 'generating' && <div style={{ fontSize: 11, color: 'var(--sf)', marginTop: 3 }}>⏳ {t('reportPage.generatingShort')}</div>}
                {st === 'error' && <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 3 }}>⚠ {t('reportPage.linkExpiredShort')}</div>}
              </div>
              <span className="jt-rprice">₹{item.price_inr}</span>
              {st === 'ready' ? (
                <button className="btn btn-sm btn-primary" onClick={() => handleDownload(type)}>{t('reportPage.download')}</button>
              ) : st === 'error' ? (
                // Bug fix (11 Jul 2026): this used to fall into the generic
                // "Buy" branch below, which looked like the purchase had
                // vanished after a failed/expired download. It hadn't --
                // the report was already generated, only the download link
                // needed refreshing. Regenerate re-triggers generation via
                // the same handleBuy() flow (admin_free-aware, same as the
                // primary card's Regenerate), it's just labelled correctly now.
                <button className="btn btn-sm btn-secondary" onClick={() => handleBuy(type)}>↻ {t('reportPage.regenerate')}</button>
              ) : (
                <button className="btn btn-sm btn-secondary" disabled={st === 'generating'} onClick={() => handleBuy(type)}>
                  {st === 'generating' ? '…' : t('reportPage.buy')}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
